/**
 * Created by rathompson on 8/2/23
 * File: IpDeleteModal.tsx
 * Project: cloud-mgmt-modules ©
 * ©
 */


import type { VFC } from 'react'
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalProps,
  Button,
  Bold, Title
} from '@panwds/react-ui'
import {
  BUTTON_CANCEL
} from '../../../common/constants/LabelConstants'
import {
  BUTTON_OK,
  LABEL_IP_DELETE_WARNING,
  LABEL_WARNING,
  TITLE_ARE_YOU_SURE,
  WARNING_IP_DELETE_LOCKOUT,
} from '../../constants/LabelConstants'
import type { TrustedIpTableRow } from '../table/TrustedIpTable'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import GlobalContext from '../../../common/context/GlobalContext'
import ScreenLoader from '../../../common/components/screenLoader/ScreenLoader'
import IAMService from '../../../iam/services/IAMService'
import { setAllowedIps } from '../../../common/reducers/actions/IAMReducer.actions'
import { useIpRestrictionContext } from '../../../common/context/IpRestrictContext'
import StringTemplate from '../../../common/utils/StringTemplate'

const IpDeleteModal: VFC<ModalProps & {
  closeModal: () => void,
  selected: TrustedIpTableRow[],
  callback: Function
}> = ({ closeModal, isOpen, selected, callback }) => {

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [warning, setWarning] = useState('')

  const { iamReducer, treeReducer, dispatch } = useContext(GlobalContext)
  const { selectedTreeNode } = treeReducer
  const { allowedIps } = iamReducer

  const { checkIpRestricted } = useIpRestrictionContext()

  const ip_addresses = useMemo(() => allowedIps
    .filter(({ inherited_from }) => inherited_from === selectedTreeNode?.id)
    .map(({ ip }) => ip), [selectedTreeNode, allowedIps])

  const ipsToRemove = useMemo(() => selected.map(item => item.ip), [selected])
  const remainingIps = useMemo(() => ip_addresses.filter(ip => !ipsToRemove.includes(ip)), [ip_addresses, ipsToRemove])

  const dryRun = useCallback(async () => {
    try {
      setWarning('')
      if (remainingIps.length) {
        const { data } = await IAMService.ipRestrictionPolicy.dryRun(selectedTreeNode?.id, {
          ip_addresses: remainingIps
        })
        const { clientIP, isClientIPAllowed } = data
        if (!isClientIPAllowed) {
          setWarning(StringTemplate(WARNING_IP_DELETE_LOCKOUT, [clientIP]) as string)
        }
      }
    }
    catch (error: any) {
      setError(error.message)
    }
  }, [remainingIps, selectedTreeNode?.id])

  useEffect(() => {
    let active = true
    setLoading(true)
    dryRun().finally(() => {
      if (active) {
        setLoading(false)
      }
    })
    return () => {
      active = false
    }
  }, [dryRun])

  const onSubmit = useCallback(async () => {
    try {
      const { data } = remainingIps.length ? await IAMService.ipRestrictionPolicy.update(selectedTreeNode?.id, {
        ip_addresses: remainingIps
      }) : await IAMService.ipRestrictionPolicy.delete(selectedTreeNode?.id)

      dispatch(setAllowedIps(data.ip_addresses))
      setLoading(false)
      closeModal()
      callback(ipsToRemove.length)
    }
    catch (error: any) {
      checkIpRestricted(error)
      setLoading(false)
      setError(error.message)
    }
  }, [callback, checkIpRestricted, closeModal, dispatch, ipsToRemove.length, remainingIps, selectedTreeNode?.id])

  return (
    <Modal
      isOpen={isOpen}
      onClose={closeModal}
      size='sm'
      style={{ minWidth: '500px' }}
    >
      <ModalHeader title={TITLE_ARE_YOU_SURE}/>
      <ModalBody addClassName='cm-flex cm-flex-col cm-gap-2'>
        {error && <Bold appearance='semibold' addClassName='tw-text-error'>{error}</Bold>}
        {warning && (
          <div className='cm-flex cm-gap-2 cm-flex-col'>
            <Title level={2} size='sm' addClassName='cm-text-warning'>{LABEL_WARNING}</Title>
            <Bold appearance='semibold'>{warning}</Bold>
          </div>
        )}
        {loading && <ScreenLoader appearance='light'/>}
        {LABEL_IP_DELETE_WARNING}
      </ModalBody>
      <ModalFooter>
        <Button
          onClick={closeModal}
        >
          {BUTTON_CANCEL}
        </Button>
        <Button
          appearance='primary'
          onClick={onSubmit}
        >
          {BUTTON_OK}
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export default IpDeleteModal
